import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Modal } from 'is24-corecss'
import { AttachmentDataWithRequiredAttachment } from '../../../../../../../../@types/types'
import MultimediaDetailsModalContent from '../modalContent/MultimediaDetailsModalContent'
import { useDisableBodyScroll } from '../../../../../../../common/hooks/useDisableBodyScroll'
import { isWebView } from '../../../../../../../../utils/navigation'
import styles from './MultimediaDetailsModal.module.css'

export interface MultimediaDetailsModalProps {
  attachments: AttachmentDataWithRequiredAttachment[]
  attachmentIndex: number
  onTitleChange: (attachment: AttachmentDataWithRequiredAttachment) => void
  onFloorPlanStatusChange: (attachment: AttachmentDataWithRequiredAttachment) => void
  onDelete: (index: number) => void
  isOpen: boolean
  closeModal: () => void
  onPreviousImage: () => void
  onNextImage: () => void
  showButtonPreviousImage: boolean
  showButtonNextImage: boolean
  headerAction?: 'close' | 'save'
}

const MultimediaDetailsModal: React.FC<MultimediaDetailsModalProps> = ({
  attachments,
  attachmentIndex,
  onTitleChange,
  onFloorPlanStatusChange,
  onDelete,
  isOpen,
  closeModal,
  onPreviousImage,
  onNextImage,
  showButtonPreviousImage,
  showButtonNextImage,
  headerAction = 'close',
}) => {
  const { t } = useTranslation()
  useDisableBodyScroll(isOpen)

  const secondaryBtnContent = useMemo(() => {
    return showButtonPreviousImage
      ? t('wizard.screen.multimediaNewModalContent.button.previousImage', 'Previous image')
      : undefined
  }, [t, showButtonPreviousImage])

  const secondaryBtnOnClick = useMemo(() => {
    return showButtonPreviousImage ? onPreviousImage : undefined
  }, [showButtonPreviousImage, onPreviousImage])

  const primaryBtnContent = useMemo(() => {
    return showButtonNextImage
      ? t('wizard.screen.multimediaNewModalContent.button.nextImage', 'Next image')
      : t('wizard.screen.multimediaNewModalContent.button.ready', 'Ready')
  }, [t, showButtonNextImage])

  const primaryBtnOnClick = useMemo(() => {
    return showButtonNextImage ? onNextImage : closeModal
  }, [closeModal, showButtonNextImage, onNextImage])

  const onDeleteHandler = useCallback(() => {
    if (attachments.length === 1) {
      closeModal()
    }
    onDelete?.(attachmentIndex)
  }, [attachments, attachmentIndex, closeModal, onDelete])

  const modalTitle = isWebView()
    ? t('wizard.screen.multimediaNewModalContent.mobile.title', 'Picture details')
    : t('wizard.screen.multimediaNewModalContent.title', 'Picture details')

  const currentAttachment = attachments[attachmentIndex]

  if (!currentAttachment) {
    return null
  }

  return (
    <Modal.Root isOpen={isOpen} onClose={closeModal}>
      <Modal.Backdrop>
        <Modal.Surface>
          <Modal.Header>
            <Modal.TitleGroup>
              <Modal.Title>{modalTitle}</Modal.Title>
            </Modal.TitleGroup>
            {headerAction === 'save' ? (
              <Link as='button' variant='blue' className={styles.saveLink} onClick={closeModal}>
                {t('wizard.screen.multimedia.save', 'Save')}
              </Link>
            ) : (
              <Modal.CloseButton aria-label='close' />
            )}
          </Modal.Header>
          <Modal.Body className={styles.modalBody}>
            <MultimediaDetailsModalContent
              attachment={currentAttachment}
              index={attachmentIndex}
              attachmentsAmount={attachments.length}
              onTitleChange={onTitleChange}
              onFloorPlanStatusChange={onFloorPlanStatusChange}
              onDelete={onDeleteHandler}
            />
          </Modal.Body>
          <Modal.Footer>
            {secondaryBtnContent ? (
              <Modal.SecondaryFooterButton label={secondaryBtnContent} onClick={secondaryBtnOnClick} />
            ) : null}
            <Modal.PrimaryFooterButton label={primaryBtnContent} onClick={primaryBtnOnClick} />
          </Modal.Footer>
        </Modal.Surface>
      </Modal.Backdrop>
    </Modal.Root>
  )
}

export default MultimediaDetailsModal
